/**
 * Das Blatt zum Fund des Tages. (08.09.2026)
 *
 * Martin am 07.09.: *„Kann Vespera einmal am Tag zumindest einen wichtigen
 * Beitrag liefern … weil ich sehr interessiert finde, dort hineinzugehen in
 * diese Themen."*
 *
 * ⚠️ Bewusst ein eigenes, schmales Blatt und keine Chat-Nachricht. Ein Fund
 *    ist zum Lesen da, nicht zum Beantworten; im Chat waere er nach drei
 *    Zuegen weggescrollt. Wer darueber sprechen will, kann es unten mit
 *    einem Tipp — dann geht er den gewohnten Weg.
 */
import { buildPageHeader } from "./sheet-ui.js";
import { vorleseText } from "../../shared/titel-doppelt.js";
import { renderMarkdown } from "./markdown.js";

const BLATT_ID = "fund-sheet";

interface FundAnzeige {
  titel: string;
  text: string;
  herkunft?: FundHerkunft;
  /**
   * Gleich abspielen — der Knopf „Anhören" im Rückblick (23.09.2026).
   * ⚠️ Ob Ton kommt, entscheidet der Browser (Geste); anbieten, nicht
   *    versprechen — derselbe Satz wie beim Spiegel.
   */
  abspielen?: boolean;
}

/** Woraus der Fund gebaut ist — kommt so aus /api/fund/heute. */
export interface FundHerkunft {
  zeilen: number;
  eigene: number;
  von: string;
  bis: string;
}

/**
 * Der Satz unter dem Titel. (21.09.2026)
 *
 * Martin: *„Woraus setzt sich Fund des Tages zusammen? … Weil ich den Sinn
 * noch nicht so ganz erfasst habe, zumindest so wie er jetzt dargestellt
 * wird."*
 *
 * ⚠️ Rein und exportiert, damit der Satz geprueft werden kann — und weil er
 *    ohne Zahlen anders lauten muss als mit.
 */
export function herkunftSatz(h: FundHerkunft | undefined): string {
  if (!h || h.zeilen <= 0) return "";
  const spanne = h.von && h.bis && h.von !== h.bis ? ` von ${h.von} bis ${h.bis}` : "";
  const eigen =
    h.eigene > 0
      ? `, ${h.eigene} davon in deinen eigenen Worten`
      : ", alle in der Nacherzählung des Systems";
  return `Gebaut aus ${h.zeilen} Einträgen deines Gedächtnisses${spanne}${eigen}.`;
}

/**
 * Der geteilte Vorlese-Player. (08.09.2026)
 *
 * Martin: *„Diese Seite ist für mich so schwer verwertbar, denn
 * grundsätzlich lese ich nichts und lasse mir die Dinge vorlesen."*
 *
 * ⚠️ Wird von app.ts hereingereicht, weil `buildNarrationPlayer` dort lebt —
 *    dasselbe Muster wie bei Projekten und Dateien. Ein eigener Player hier
 *    waere der vierte, der dasselbe tut, und die Abspielleiste ueber der
 *    Fusszeile wuesste nichts von ihm.
 */
type FundSpieler = (opts: {
  endpoint: string;
  getBody: (voiceId: string) => Record<string, unknown> | null;
  titel?: string;
  sectionable?: boolean;
}) => {
  el: HTMLElement;
  setSections?: (items: Array<{ title: string; text: string }>) => void;
  setReady?: (ok: boolean) => void;
  prefetch?: () => void;
  reset?: () => void;
  /** Von außen starten (NarrationPlayer.play, Ticket 2395). */
  play?: () => void;
};

let spielerBauer: FundSpieler | null = null;

/** Einmal beim Start aus app.ts setzen. */
export function setzeFundSpieler(bauer: FundSpieler): void {
  spielerBauer = bauer;
}

/** Das Blatt schliessen. */
function schliesseFund(): void {
  document.getElementById(BLATT_ID)?.remove();
}

/**
 * Den Fund zeigen.
 *
 * ⚠️ Als GELESEN wird er hier gemeldet, nicht beim Anzeigen im Zuhause.
 *    Dieselbe Lehre wie bei der Tugend (08.09.): Gezaehlt wird, was er tut
 *    — nicht, was ihm hingelegt wurde.
 */
export function zeigeFund(fund: FundAnzeige): void {
  if (document.getElementById(BLATT_ID)) return;
  window.dispatchEvent(new CustomEvent("voice-v8:page-open"));

  const blatt = document.createElement("div");
  blatt.className = "sheet fund-sheet page-sheet";
  blatt.id = BLATT_ID;
  blatt.setAttribute("role", "dialog");
  blatt.setAttribute("aria-modal", "true");

  const karte = document.createElement("div");
  karte.className = "sheet-card";
  const koerper = document.createElement("div");
  koerper.className = "fund-body page-body";
  karte.appendChild(koerper);
  blatt.appendChild(karte);
  karte.prepend(buildPageHeader("Fund des Tages", schliesseFund));

  /*
   * ⚠️ VOR dem Vorlese-Knopf und in derselben Spalte wie der Text: wer das
   *    Blatt oeffnet, soll die Frage „woraus ist das gebaut?" beantwortet
   *    bekommen, ohne danach zu suchen. Ohne Zahlen bleibt die Zeile weg —
   *    ein leerer Platzhalter erklaert nichts.
   */
  const satz = herkunftSatz(fund.herkunft);
  if (satz) {
    const woher = document.createElement("p");
    woher.className = "fund-herkunft";
    woher.textContent = satz;
    koerper.appendChild(woher);
  }

  /*
   * ⚠️ Die Leiste steht VOR dem Text, nicht darunter. Wer nicht liest,
   *    soll sie nicht erst suchen muessen — dieselbe Anordnung wie im
   *    Spiegel.
   */
  if (spielerBauer) {
    const spieler = spielerBauer({
      endpoint: "/api/fund/narrate",
      titel: "Fund des Tages",
      // Der Fund ist ein Stueck, kein Buch: kein Blaettern noetig.
      sectionable: false,
      /*
       * ⚠️ 10.09.2026 — Martin: „die Überschrift wird doppelt gelesen."
       *    Der Fund kommt vom Modell als Markdown, dessen erste Zeile IST
       *    der Titel („# Vier Stillen, eine davon gelingt"). Hier stand er
       *    trotzdem noch einmal davor. `vorleseText` sieht nach, statt zu
       *    raten — Texte ohne eigene Überschrift bekommen die Ansage weiter.
       */
      getBody: (voiceId) => ({
        text: vorleseText(fund.titel, fund.text),
        voiceId,
      }),
    });
    const reihe = document.createElement("div");
    reihe.className = "fund-vorlesen";
    reihe.appendChild(spieler.el);
    koerper.appendChild(reihe);
    spieler.setReady?.(true);
    // Die Vertonung im Hintergrund anstossen — der erste Druck spielt sofort.
    spieler.prefetch?.();
    if (fund.abspielen) spieler.play?.();
  }

  const text = document.createElement("div");
  text.className = "fund-text";
  renderMarkdown(text, fund.text);
  koerper.appendChild(text);

  const reden = document.createElement("button");
  reden.type = "button";
  reden.className = "btn btn--ghost btn--sm fund-reden";
  reden.textContent = "Darüber sprechen";
  reden.addEventListener("click", () => {
    schliesseFund();
    /*
     * ⚠️ 08.09.2026 — EIN GANZER SATZ. Hier stand `Zu deinem Fund „…": ` —
     *    ein Vorspann, den Martin fortsetzen sollte. Nur SENDET
     *    `voice-v8:ask-vespera` sofort (siehe app.ts), es ist kein
     *    Vorschlagsfeld. Vespera bekam ein Bruchstueck und antwortete
     *    zu Recht: *„Deine Nachricht bricht nach dem Doppelpunkt ab."*
     *
     * ⚠️ Der Titel steht mit im Satz, damit die Frage auch dann eindeutig
     *    ist, wenn spaeter mehrere Funde im Gespraech liegen. Den TEXT
     *    braucht sie nicht mitzuschicken — der steht seit heute im
     *    Kontext des Gespraechs (server/fund.ts, fundBlock).
     */
    window.dispatchEvent(
      new CustomEvent("voice-v8:ask-vespera", {
        detail: {
          text: `Erzähl mir mehr zu deinem Fund „${fund.titel}" — was steckt da für dich drin?`,
        },
      }),
    );
  });
  koerper.appendChild(reden);

  document.body.appendChild(blatt);

  void fetch("/api/fund/gelesen", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  }).catch(() => undefined);

  const beiTaste = (ev: KeyboardEvent): void => {
    if (ev.key !== "Escape") return;
    schliesseFund();
    document.removeEventListener("keydown", beiTaste);
  };
  document.addEventListener("keydown", beiTaste);
}
